import { ThemeOptions } from '@mui/material';

export enum Company {
  DDS = 'DD&S Express, Inc.',
  Eastern = 'Eastern Express, Inc.',
  Horizon = 'Horizon Freight System, Inc.',
  Kaplan = 'Kaplan Trucking Company',
  TRX = 'TRX Trucking Company',
  PFQ = 'PFQ Companies'
}

/**
 * State object containing AppSettings. Allowing null to instantiate redux slice without default values.
 */
export type AppSettingsState = AppSettings | null;

/**
 * Object representing appSettings.json structure
 */
export interface AppSettings {
  /** Currently running Company */
  company: Company;
  /** Used for specifying non-production environment. Omitted in production appsettings. */
  environment?: string;
  /** Interval in seconds that a fetch request will get the version.json */
  versionFetchIntervalSeconds: number;
  /** Base URL of the Trinium API */
  triniumApiBaseUrl: string;
  azure: {
    appInsightsConnectionString: string;
    clientId: string;
    authority: string;
    redirectUri: string;
    scope: string;
    /** Azure Maps account subscription key used by the Driver Route map. Required when features.driverRoute is enabled. */
    mapsSubscriptionKey?: string;
  };
  defaultDebounceMs: number;
  features?: {
    /** Enables all functionality related to Documents (View/Print, Unscan, Email) */
    documents?: boolean;
    /** Enables functionality for Document Uploading */
    uploadDocuments?: boolean;
    /** Enables functionality for Document Removal/Unscanning */
    removeDocuments?: boolean;
    /** Enables content in the Charges & Payments tab of Consignment Details */
    chargesAndPayments?: boolean;
    /** Enables content in the Order tab of Consignment Details */
    order?: boolean;
    /** Enables functionality for Document Control */
    documentControl?: boolean;
    /** Enables functionality for Street Turn */
    streetTurn?: boolean;
    /** Enables features  */
    addConsignment?: boolean;
    /** Enable the EDI Load Tenders Navbar item to get to the Load Tenders page */
    ediLoadTendersPage?: boolean;
    /** Flag to display the Cancel Order/Consignment button on Order Entry */
    cancelOrder?: boolean;
    /** Flag to enable the Copy Order/Consignment button on Order Entry */
    copyOrder?: boolean;
    /** Flag to enable the Agent Settlements reporting tool */
    agentSettlements?: boolean;
    /** Enables functionality for adding multiple containers */
    addMultiContainer?: boolean;
    /** Flag to enable the Dispatch Board query editor (toolbar button and the Edit Board Queries page) */
    editBoardQueries?: boolean;
    /** Enables the View Driver Route action on the Dispatch Board (requires azure.mapsSubscriptionKey) */
    driverRoute?: boolean;
  };
}

export type CompanySettings = Record<
  string,
  {
    company: Company;
    domain: string;
    supportPhoneNumber: string;
    supportExtension?: string;
    theme: ThemeOptions;
    divisions: DivisionSettings;
    /** Report shown on the Power BI splash page of terminals flagged usePowerBISplash */
    powerBiSplash?: PowerBiReportSettings;
  }
>;

export interface PowerBiReportSettings {
  workspaceId: string;
  reportId: string;
  pageName: string;
}

export type DivisionSettings = Record<
  string,
  {
    theme: ThemeOptions;
  }
>;

export enum AppLocalStorageKeys {
  NavbarOpen = 'NavbarOpen',
  GridDensity = 'GridDensity',
  ResearchActiveFilterFields = 'ResearchActiveFilterFields',
  DispatchHiddenColumns = 'DispatchHiddenColumns',
  DispatchColumnWidths = 'DispatchColumnWidths'
}

/**
 * Represents a string valued input that can be null. The API references null as committing the field to a blank value
 * where undefined is removed from the payload because the API will ignore it alltogether.
 */
export type MutableNullableString = string | null | undefined;

/**
 * Represents a string valued input that is required to have a value and cannot be set to null;
 */
export type MutableRequiredString = string | undefined;
